import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, TextField, InputAdornment
} from '@mui/material';
import { ArrowBack, Refresh, Search, HistoryEdu } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

export default function AuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      let url = `/admin/audit-logs?page=${page}`;
      if (actionFilter) url += `&action=${encodeURIComponent(actionFilter)}`;
      const data = await apiRequest(url);
      setLogs(data.logs || []);
    } catch { setLogs([]); }
    setLoading(false);
  }, [page, actionFilter]);

  useEffect(() => { setLoading(true); fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter(l =>
    search === '' || (l.user_name || '').toLowerCase().includes(search.toLowerCase())
      || (l.action || '').toLowerCase().includes(search.toLowerCase())
      || (l.resource_id || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (t) => new Date(t).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Audit Log" size="small" variant="outlined" icon={<HistoryEdu sx={{ fontSize: 14 }} />} />
            </Stack>
            <Tooltip title="Refrescar"><IconButton onClick={fetchLogs}><Refresh /></IconButton></Tooltip>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField size="small" placeholder="Buscar por usuario, acción o recurso..." value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94A3B8' }} /></InputAdornment> }}
            />
            <TextField size="small" placeholder="Filtrar acción (ej: config_update)" value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 200 }}
            />
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No hay registros</Typography></Box>
          ) : (
            filtered.map((l, idx) => (
              <React.Fragment key={l.id}>
                {idx > 0 && <Divider />}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ minWidth: 100 }}>
                      <Chip label={l.action} size="small" sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 600, fontSize: 10 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{l.user_name || 'Sistema'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(l.timestamp)}
                        {l.resource_type && ` · ${l.resource_type}`}
                        {l.resource_id && `: ${l.resource_id.substring(0, 20)}`}
                        {l.ip_address && ` · ${l.ip_address}`}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </React.Fragment>
            ))
          )}
        </Paper>

        {logs.length >= 50 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            {page > 0 && <Chip label="← Anterior" onClick={() => setPage(p => p - 1)} clickable />}
            <Chip label="Siguiente →" onClick={() => setPage(p => p + 1)} clickable />
          </Stack>
        )}
      </Container>
    </Box>
  );
}
